/**
 * Enrolling a beta tester: a licence granted rather than sold.
 *
 * Three verbs, because "who is being given something" has three answers:
 *
 * - **An account** gets a personal licence. Their Airlock runs.
 * - **An organization** gets a window on its seats. Everybody holding one —
 *   owner, administrator, member alike — gets a key *and* its workspaces.
 * - **One member of an organization** gets a window on their own seat, and
 *   nobody else there gets anything. A team of thirty piloting with four is the
 *   case: opening the whole organization would hand out twenty-six keys nobody
 *   agreed to give.
 *
 * The third is `BetaSeats` below, and it is the same grant the organization's
 * own page offers on each of its members — reached from either end, because
 * support starts from an address as often as from a tenant. It was once refused
 * outright: a seat's access used to be read off the **organization's** dates
 * alone, so a member granted a licence inside an unpaid organization had an app
 * that ran and a Workspaces page that answered 404. `seatsHeld` now takes the
 * better of the organization's window and the seat's own, so the tester gets
 * the whole product and the twenty-six others still get nothing.
 *
 * Granted again, all three extend rather than restart: a tester given another
 * month at the end of one does not lose the days they had left.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { Card, GhostButton, Input } from '../components/primitives';
import { useConfirm } from '../components/Modal';
import { api, ApiError } from '../lib/api';
import { proseDay } from '../lib/format';
import type { AdminAccountSeat } from '../lib/types';
import { SerialLine, type SerialActions } from './AdminSerials';

/** Long enough for a real beta, short enough that a typo is not a decade. */
const MAX_DAYS = 365;

export function BetaGrant({
  kind,
  id,
  /** What this grant is about, for the button's own words. */
  subject,
  /** Why the verb is not offered, where it is not. */
  refuse,
  onDone
}: {
  kind: 'account' | 'organization';
  id: string;
  subject: string;
  refuse?: string;
  onDone: () => void | Promise<void>;
}): React.JSX.Element {
  const [days, setDays] = useState('90');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, ask] = useConfirm();

  const grant = async (): Promise<void> => {
    const asked = Number(days);
    if (!daysValid(days)) return;
    const agreed = await ask({
      title: `Give ${subject} ${asked} days of Airlock?`,
      lede: 'With no payment behind it, and nothing on the invoice. Granted again later it adds to whatever is left rather than starting over.',
      confirm: `Grant ${asked} days`
    });
    if (!agreed) return;
    setBusy(true);
    setNote(null);
    try {
      const answer = await api.post<{ message: string }>('/admin/actions', {
        action: kind === 'account' ? 'grant-beta' : 'beta-org',
        ...(kind === 'account' ? { userId: id } : { orgId: id }),
        days: asked
      });
      setNote(answer.message);
      await onDone();
    } catch (failure) {
      setNote(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding={24} style={{ marginTop: 20, maxWidth: 640 }}>
      {dialog}
      <div style={{ fontSize: 15, fontWeight: 600 }}>Beta access</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '10px 0 0', lineHeight: 1.6 }}>
        {kind === 'account'
          ? 'A personal licence with no payment behind it. The key is perpetual, as every key here is — what ends is the entitlement, which can be moved or revoked from this page without the tester reinstalling anything.'
          : 'Opens this organization’s seats for a while, with no subscription. Every held seat gets a key, and its workspaces open. To pilot with a few people rather than the whole team, give a beta to their seats one at a time under Administrative actions below — the rest of the team is left as it is.'}
      </p>
      {refuse ? (
        <p style={{ fontSize: 13.5, color: color.warnText, margin: '14px 0 0', lineHeight: 1.6 }}>{refuse}</p>
      ) : (
        <>
          <DaysSentence
            id={`beta-days-${id}`}
            before="For"
            days={days}
            onDays={setDays}
            action={
              <HeldGhost reason={daysValid(days) ? undefined : FIX_DAYS} onClick={() => void grant()}>
                {busy ? 'Granting' : 'Grant'}
              </HeldGhost>
            }
          />
          <p style={{ fontSize: 12.5, color: color.inkFaint, margin: '12px 0 0', lineHeight: 1.6 }}>
            Granted again, it adds to whatever is left rather than starting over. Nothing here is billed, and it is worth
            nothing in the revenue totals. To end one early, revoke its serial.
          </p>
        </>
      )}
      <Note text={note} />
    </Card>
  );
}

/** What came back from the last grant, said where it was asked for. */
function Note({ text }: { text: string | null }): React.JSX.Element | null {
  if (!text) return null;
  return (
    <p
      style={{
        fontSize: 13,
        color: color.inkBody,
        background: color.surfaceAlt,
        borderRadius: radius.md,
        padding: '10px 12px',
        margin: '16px 0 0',
        lineHeight: 1.55
      }}
    >
      {text}
    </p>
  );
}

/**
 * Why a seat cannot be given a beta, or null where it can.
 *
 * Every one of these is a refusal the Worker makes too — `grantBetaSeat` is the
 * authority, and saying it here is only so the page does not offer a button
 * whose answer is already known. A seat with no reason is offered, and if the
 * Worker still refuses, its message is what the page shows.
 */
function refusalFor(seat: AdminAccountSeat): string | null {
  if (seat.deactivated) return 'This organization is deactivated. Nothing it holds grants anything until that is undone.';
  if (seat.paying) {
    return 'This organization pays for its seats. The next invoice writes its dates over every seat it holds, this one included — extend the serial with Grant 7 days on its line instead.';
  }
  if (seat.status === 'suspended') return 'This member is deactivated here. Reactivate them first, and a beta can then be granted.';
  return null;
}

/**
 * A beta on one person's seat, asked from the account rather than the tenant.
 *
 * The same grant `AdminOrgDetail` offers on each of its members, and the same
 * `beta-seat` action behind it — offered here because support arrives at an
 * address at least as often as at an organization, and having found the person
 * should not then have to find which of their organizations to open. One field
 * for however many seats they hold: how long the pilot runs has one answer, and
 * the rows below it name the organization each grant lands in.
 *
 * Nothing about any organization moves. Its dates, its plan and the seats it
 * bought are left where they are, so a pilot inside a company that has bought
 * nothing stays a pilot — the tester is given a seat to hold the window, free
 * and outside anything the organization has paid for.
 */
export function BetaSeats({
  userId,
  /** The address being given something, for the confirmation's own words. */
  subject,
  seats,
  serials,
  onDone
}: {
  userId: string;
  subject: string;
  seats: AdminAccountSeat[];
  /** Grant 7 days and Revoke on each seat's serial line (§47.5). */
  serials: SerialActions;
  onDone: () => void | Promise<void>;
}): React.JSX.Element {
  const [days, setDays] = useState('90');
  const [note, setNote] = useState<string | null>(null);
  /** The organization a grant is in flight for, so one row at a time says so. */
  const [busy, setBusy] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();

  const grant = async (seat: AdminAccountSeat): Promise<void> => {
    const asked = Number(days);
    if (!daysValid(days)) return;
    const agreed = await ask({
      title: `Give ${subject} ${asked} days of Airlock?`,
      lede: `In ${seat.orgName}, with no payment behind it. They are given a seat to hold it, free and outside anything the organization has bought, and everybody else there stays as they are.`,
      confirm: `Grant ${asked} days`
    });
    if (!agreed) return;
    setBusy(seat.orgId);
    setNote(null);
    try {
      const answer = await api.post<{ message: string }>('/admin/actions', {
        action: 'beta-seat',
        orgId: seat.orgId,
        userId,
        days: asked
      });
      setNote(answer.message);
      await onDone();
    } catch (failure) {
      setNote(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  const open = seats.some((seat) => refusalFor(seat) === null);

  return (
    <Card padding={24} style={{ marginTop: 20, maxWidth: 640 }}>
      {dialog}
      <div style={{ fontSize: 15, fontWeight: 600 }}>Beta access</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '10px 0 0', lineHeight: 1.6 }}>
        This account holds a seat, so the window goes on the seat: their app runs and that organization’s workspaces open
        for them, while everybody else there is left exactly as they are. It is the grant the organization’s own page
        makes, given from this end.
      </p>
      {open && (
        <DaysSentence
          id={`beta-seat-days-${userId}`}
          before="A beta seat runs for"
          after="and extends where one is already running."
          days={days}
          onDays={setDays}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
        {seats.map((seat) => {
          const refusal = refusalFor(seat);
          return (
            <SerialLine
              key={seat.orgId}
              serial={seat.serial}
              actions={serials}
              who={
                <>
                  {seat.orgName}
                  <span style={{ color: color.inkFaint }}> · {seat.role}</span>
                </>
              }
              detail={
                <>
                  {seat.plan === 'beta' && seat.paidThrough ? (
                    <span style={{ display: 'block', fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                      beta, {seat.access === 'active' ? 'to' : seat.access === 'grace' ? 'grace since' : 'ended'}{' '}
                      {proseDay(seat.paidThrough)}
                    </span>
                  ) : null}
                  {refusal && (
                    <span style={{ display: 'block', fontSize: 12.5, color: color.warnText, marginTop: 5, lineHeight: 1.55 }}>
                      {refusal}
                    </span>
                  )}
                </>
              }
              beta={
                refusal ? undefined : (
                  <HeldGhost reason={daysValid(days) ? undefined : FIX_DAYS} onClick={() => void grant(seat)}>
                    {busy === seat.orgId ? 'Granting' : seat.plan === 'beta' ? `+${days} days` : `Beta ${days} days`}
                  </HeldGhost>
                )
              }
            />
          );
        })}
      </div>
      <p style={{ fontSize: 12.5, color: color.inkFaint, margin: '16px 0 0', lineHeight: 1.6 }}>
        Granted again, it adds to whatever is left rather than starting over. Nothing here is billed, and it is worth
        nothing in the revenue totals. To end one early, revoke its serial — the seat it was given goes back with it.
      </p>
      <Note text={note} />
    </Card>
  );
}

/** Why a button that depends on the number is held (§47.7). */
export const FIX_DAYS = 'Fix the number of days first';

/** Whether a number of days is one a grant takes: a whole number from 1 to 365. */
export function daysValid(days: string): boolean {
  const asked = Number(days);
  return days.trim() !== '' && Number.isInteger(asked) && asked >= 1 && asked <= MAX_DAYS;
}

/**
 * A number set in the code's own sentence (redesign §47.7): « For [90 days] »,
 * « A beta seat runs for [90 days] and extends where one is already running. »
 * The field is 120 with its unit as a suffix and the digits in mono on the
 * right; the sentence's action sits at the right of the line; the error is
 * under it and is read again at every keystroke.
 */
export function DaysSentence({
  id,
  before,
  after,
  days,
  onDays,
  action
}: {
  id: string;
  before: string;
  after?: string;
  days: string;
  onDays: (days: string) => void;
  action?: React.ReactNode;
}): React.JSX.Element {
  const wrong = !daysValid(days);
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label htmlFor={id} style={{ fontSize: 13.5, color: color.inkMuted }}>
          {before}
        </label>
        <span style={{ position: 'relative', display: 'inline-flex', width: 120 }}>
          <Input
            id={id}
            className="al-days"
            type="number"
            min={1}
            max={MAX_DAYS}
            value={days}
            aria-invalid={wrong || undefined}
            aria-describedby={wrong ? `${id}-error` : undefined}
            onChange={(event) => onDays(event.target.value)}
            style={{
              width: 120,
              height: 36,
              paddingRight: 44,
              fontFamily: font.mono,
              textAlign: 'right',
              borderColor: wrong ? color.red : undefined
            }}
          />
          <span
            aria-hidden="true"
            style={{ position: 'absolute', right: 11, top: 0, bottom: 0, display: 'flex', alignItems: 'center', fontSize: 13, color: color.inkSubtle, pointerEvents: 'none' }}
          >
            days
          </span>
        </span>
        {after && <span style={{ fontSize: 13.5, color: color.inkMuted }}>{after}</span>}
        <span style={{ flex: 1 }} />
        {action}
      </div>
      {wrong && (
        <div id={`${id}-error`} role="alert" style={{ fontSize: 12.5, color: color.badText, marginTop: 6 }}>
          Give a number of days, from 1 to {MAX_DAYS}.
        </div>
      )}
    </div>
  );
}

/** A secondary S held at 40 % with its reason when what it depends on is wrong. */
export function HeldGhost({
  reason,
  onClick,
  children
}: {
  reason?: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span title={reason} style={{ display: 'inline-flex' }}>
      <GhostButton size="sm" onClick={reason ? () => undefined : onClick} disabled={Boolean(reason)}>
        {children}
      </GhostButton>
    </span>
  );
}
